import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import type React from 'react';
import * as yup from 'yup';

import HookFormResolver, { type HookFormInvalidHandler } from '../Form/HookForm/hookFormResolver';

import { Sections } from './Form/sections';
import { createDefaultValesTaxReturn, createTaxReturnRequest } from './creatorTaxReturnRequest';
import type {
  DefinitionComponent,
  FormDefinitionLike,
  HookFormValidationMode,
  Jurisdiction,
  TaxReturnCalculationConnection,
  TaxReturnCell,
  TaxReturnRequestRuntime
} from './types';

export interface TaxReturnFormLoadingState {
  active: boolean;
  title: string;
}

interface TaxReturnFormRuntimeRenderProps {
  readOnly: boolean;
  setLoading: React.Dispatch<React.SetStateAction<TaxReturnFormLoadingState>>;
  onCellChanged?: () => void;
}

interface TaxReturnFormContentWrapperProps {
  children: ReactNode;
  loading: TaxReturnFormLoadingState;
}

export interface TaxReturnFormProps<
  TRequest,
  TDefinition extends FormDefinitionLike,
  TPeriod extends object,
  TCell extends TaxReturnCell = TaxReturnCell,
  TSection extends DefinitionComponent = DefinitionComponent,
  TCellDefinition extends DefinitionComponent = DefinitionComponent
> {
  version: string;
  submit: (data: TRequest) => void;
  definition?: TDefinition;
  loading: boolean;
  error?: unknown;
  connection?: TaxReturnCalculationConnection;
  createComponent: (definition: TSection) => ReactNode;
  createValidation: (definition: TCellDefinition) => any;
  clearLogRules: () => void;
  wrapProviders: (props: {
    children: ReactNode;
    runtime: TaxReturnRequestRuntime<TDefinition, TPeriod> & { readOnly: boolean };
    connection?: TaxReturnCalculationConnection;
  }) => ReactNode;
  renderRuntime: (props: TaxReturnFormRuntimeRenderProps) => ReactNode;
  wrapContent?: (props: TaxReturnFormContentWrapperProps) => ReactNode;
  formName?: string;
  taxpayerId?: string;
  amendedTaxReturnId?: string;
  readOnly?: boolean;
  period?: TPeriod;
  defaultValue?: Array<TCell>;
  jurisdictions?: Array<Jurisdiction>;
  validationMode?: HookFormValidationMode;
  onInvalid?: HookFormInvalidHandler;
  enableInitializationSpinner?: boolean;
  emptyValidationsFallback?: ReactNode;
}

export const createTaxReturnValidationSchema = <
  TDefinition extends FormDefinitionLike,
  TCellDefinition extends DefinitionComponent
>(
  definition: TDefinition,
  createValidation: (definition: TCellDefinition) => any
) => {
  const sections = Object.fromEntries(
    definition.sections.map((section) => {
      const cells = Object.assign(
        {},
        ...section.cells.map(
          (cell) => (createValidation(cell as TCellDefinition) as yup.ObjectSchema<any>).fields
        )
      );

      return [
        section.code,
        yup.object().shape({
          instances: yup.array().of(
            yup.object().shape({
              cells: yup.object().shape(cells)
            })
          )
        })
      ];
    })
  );

  return yup.object().shape({ sections: yup.object().shape(sections) });
};

export const TaxReturnForm = <
  TRequest,
  TDefinition extends FormDefinitionLike,
  TPeriod extends object,
  TCell extends TaxReturnCell = TaxReturnCell,
  TSection extends DefinitionComponent = DefinitionComponent,
  TCellDefinition extends DefinitionComponent = DefinitionComponent
>({
  version,
  submit,
  definition,
  loading,
  error,
  connection,
  createComponent,
  createValidation,
  clearLogRules,
  wrapProviders,
  renderRuntime,
  wrapContent,
  formName = 'TaxReturnForm',
  taxpayerId,
  amendedTaxReturnId,
  readOnly = false,
  period,
  defaultValue = [],
  jurisdictions = [],
  validationMode,
  onInvalid,
  enableInitializationSpinner = false,
  emptyValidationsFallback
}: TaxReturnFormProps<TRequest, TDefinition, TPeriod, TCell, TSection, TCellDefinition>) => {
  const [loadingSpinner, setLoading] = useState<TaxReturnFormLoadingState>({
    active: false,
    title: ''
  });
  const [initializing, setInitializing] = useState(false);

  useEffect(() => {
    clearLogRules();
    if (enableInitializationSpinner && !readOnly) {
      setInitializing(true);
    }
  }, [clearLogRules, enableInitializationSpinner, readOnly]);

  useEffect(() => {
    if (!enableInitializationSpinner) return;

    if (!initializing) {
      setLoading((current) =>
        current.active || current.title ? { active: false, title: '' } : current
      );
      return;
    }

    setLoading({ active: true, title: '' });

    const timer = setTimeout(() => {
      setInitializing(false);
    }, 4500);

    return () => {
      clearTimeout(timer);
    };
  }, [enableInitializationSpinner, initializing]);

  const handleCellChanged = useCallback(() => {
    if (initializing) {
      setInitializing(false);
    }
  }, [initializing]);

  const validations = useMemo(() => {
    if (!definition) return undefined;

    if (definition.sections.every((section) => section.cells.length === 0)) {
      return undefined;
    }

    return createTaxReturnValidationSchema(definition, createValidation);
  }, [createValidation, definition]);

  const hubReady = readOnly || connection !== undefined;
  if (loading || error || !definition || !hubReady) return <></>;

  if (!validations && emptyValidationsFallback) {
    return <>{emptyValidationsFallback}</>;
  }

  const runtime = {
    formVersionId: version,
    def: definition,
    taxpayerId,
    readOnly,
    period,
    amendedTaxReturnId
  } satisfies TaxReturnRequestRuntime<TDefinition, TPeriod> & { readOnly: boolean };

  const onSubmit = (form: any) => {
    submit(
      createTaxReturnRequest<TRequest>({
        formVersionId: version,
        def: definition,
        form,
        taxpayerId,
        period,
        amendedTaxReturnId
      })
    );
  };

  return wrapProviders({
    runtime,
    connection,
    children: wrapContent ? (
      wrapContent({
        loading: loadingSpinner,
        children: (
          <HookFormResolver
            validations={validations}
            initialValues={createDefaultValesTaxReturn(
              definition,
              readOnly,
              defaultValue,
              jurisdictions
            )}
            nameForm={formName}
            onSubmit={onSubmit}
            onInvalid={onInvalid}
            validationMode={validationMode}>
            {() => (
              <>
                {renderRuntime({
                  readOnly,
                  setLoading,
                  onCellChanged: enableInitializationSpinner ? handleCellChanged : undefined
                })}
                <Sections
                  def={definition as TDefinition & { sections: Array<TSection> }}
                  createComponent={createComponent}
                  readOnly={readOnly}
                  jurisdictions={jurisdictions}
                />
              </>
            )}
          </HookFormResolver>
        )
      })
    ) : (
      <>
        <HookFormResolver
          validations={validations}
          initialValues={createDefaultValesTaxReturn(
            definition,
            readOnly,
            defaultValue,
            jurisdictions
          )}
          nameForm={formName}
          onSubmit={onSubmit}
          onInvalid={onInvalid}
          validationMode={validationMode}>
          {() => (
            <>
              {renderRuntime({
                readOnly,
                setLoading,
                onCellChanged: enableInitializationSpinner ? handleCellChanged : undefined
              })}
              <Sections
                def={definition as TDefinition & { sections: Array<TSection> }}
                createComponent={createComponent}
                readOnly={readOnly}
                jurisdictions={jurisdictions}
              />
            </>
          )}
        </HookFormResolver>
      </>
    )
  });
};

export default TaxReturnForm;
